import React, { useEffect, useState } from "react";
import { Menu, X, Wrench } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { LANG_LABELS } from "@/i18n/translations";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { key: "services", href: "#services" },
  { key: "area", href: "#area" },
  { key: "about", href: "#about" },
  { key: "contact", href: "#contact" },
];

export function Header() {
  const { t, lang, setLang } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-brand-cream/80 border-b border-brand-sand"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-2.5 group"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-terracotta text-white shadow-sm">
            <Wrench className="h-4.5 w-4.5" strokeWidth={2.4} size={18} />
          </span>
          <span className="font-display text-lg md:text-xl font-semibold tracking-tight text-brand-ink">
            Christian's<span className="text-brand-terracotta">.</span>
            <span className="hidden sm:inline text-brand-aegean font-medium"> Multiservice</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <a
              key={item.key}
              href={item.href}
              data-testid={`nav-${item.key}`}
              className="px-4 py-2 text-sm font-medium text-brand-ink/80 hover:text-brand-terracotta transition-colors rounded-full hover:bg-brand-sand/60"
            >
              {t(`nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="language-switcher-trigger"
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-sand bg-white/70 px-3 h-9 text-sm font-semibold text-brand-ink hover:border-brand-terracotta transition-colors"
                aria-label="Change language"
              >
                <span className="tracking-wider">{LANG_LABELS[lang]}</span>
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[9rem]">
              {Object.keys(LANG_LABELS).map((code) => (
                <DropdownMenuItem
                  key={code}
                  data-testid={`lang-option-${code}`}
                  onClick={() => setLang(code)}
                  className={`cursor-pointer ${lang === code ? "text-brand-terracotta font-semibold" : ""}`}
                >
                  <span className="mr-2 font-mono">{LANG_LABELS[code]}</span>
                  <span className="text-brand-olive text-sm">
                    {code === "de" && "Deutsch"}
                    {code === "en" && "English"}
                    {code === "es" && "Español"}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            asChild
            data-testid="header-cta-button"
            className="hidden md:inline-flex rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white shadow-sm h-10 px-5"
          >
            <a href="#contact">{t("nav.cta")}</a>
          </Button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-full border border-brand-sand bg-white/70 text-brand-ink"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-brand-sand bg-brand-cream/95 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-5 py-3 flex flex-col">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                data-testid={`mobile-nav-${item.key}`}
                onClick={() => setOpen(false)}
                className="py-3 text-base font-medium text-brand-ink border-b border-brand-sand/60 last:border-b-0"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}
            <a
              href="#contact"
              data-testid="mobile-cta-button"
              onClick={() => setOpen(false)}
              className="mt-4 mb-2 inline-flex justify-center rounded-full bg-brand-terracotta text-white h-11 items-center font-semibold"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
